import { createSignOffTemplate, getSignOffTemplate, getSignOffTemplates, updateSignOffTemplate } from '@/api/sign-off-template.api';
import type { CreateSignOffTemplateRequest, ISignOffTemplateFilter, SignOffTemplateExpandOpts, UpdateSignOffTemplateParams } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MaybeRef } from '@vueuse/core';
import { unref } from 'vue';

export const useSignOffTemplates = (params?: MaybeRef<ISignOffTemplateFilter>, expand?: SignOffTemplateExpandOpts) => {
    return useQuery(['sign-off-templates', params, expand], () => getSignOffTemplates(unref(params), expand), {
        initialData: { signOffTemplates: [], total: 0 },
    });
};

export const useSignOffTemplate = (signOffTemplateId: MaybeRef<number>, expand?: SignOffTemplateExpandOpts) => {
    return useQuery(['sign-off-template', signOffTemplateId, expand], () => getSignOffTemplate(unref(signOffTemplateId), expand), {
        enabled: () => !!unref(signOffTemplateId),
    });
};

export const useSignOffTemplateMutations = () => {
    const queryClient = useQueryClient();

    const { mutateAsync: createSignOffTemplateMutation, isLoading: isCreating } = useMutation(
        (req: CreateSignOffTemplateRequest) => createSignOffTemplate(req),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['sign-off-templates']);
            },
        },
    );

    const { mutateAsync: updateSignOffTemplateMutation, isLoading: isUpdating } = useMutation(
        ({ signOffTemplateId, signOffTemplate }: UpdateSignOffTemplateParams) => updateSignOffTemplate(signOffTemplateId, signOffTemplate),
        {
            onSuccess: data => {
                queryClient.invalidateQueries(['sign-off-templates']);
                queryClient.invalidateQueries(['sign-off-template', data.template.signOffTemplateId]);
            },
        },
    );

    return {
        createSignOffTemplate: createSignOffTemplateMutation,
        updateSignOffTemplate: updateSignOffTemplateMutation,
        isCreating,
        isUpdating,
    };
};
